import { useEffect } from 'react';

import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

import { FcmNotificationType } from '@/modules/notifications';
import notificationService from '@/modules/notifications/firebase-notification.service';

import { getActiveTimer } from '../api/active-timer.api';
import { ACTIVE_TIMER_QUERY_KEYS } from '../constants/active-timer-query-keys';
import { ActiveTimerResponse } from '../types/dto/active-timer.dto';

// Module-level channel and singleton query client reference — shared across all
// hook instances so that N mounted components never register N listeners and
// fire N invalidateQueries calls per event.
const channel = new BroadcastChannel('active-timer-channel');
let singletonQueryClient: QueryClient | null = null;

const invalidateTimerQuery = () => {
  singletonQueryClient?.invalidateQueries({ queryKey: ACTIVE_TIMER_QUERY_KEYS.all });
};

// One BroadcastChannel listener for cross-tab sync.
channel.addEventListener('message', invalidateTimerQuery);

// One FCM listener for push notifications from the backend / other devices.
notificationService.subscribe(FcmNotificationType.ACTIVE_TIMER_UPDATED, invalidateTimerQuery);

// Force a fresh authoritative pull when the tab becomes visible again. This
// covers the un-minimize / restore-from-background case that `refetchOnWindowFocus`
// (which fires on window `focus`) can miss, and which otherwise leaves the three
// timer surfaces showing stale/divergent state until a manual refresh
// (OPS-1538/1534). We rely on the query's own `refetchOnWindowFocus` for the
// `focus` case, so we only listen to `visibilitychange` here to avoid a
// duplicate trigger. `invalidateQueries` is a no-op when no timer query is
// mounted, and TanStack dedupes if it coincides with the focus refetch.
// Registered once at module scope so N mounted components don't fire N events.
const handleTabVisible = () => {
  if (document.visibilityState === 'visible') invalidateTimerQuery();
};
document.addEventListener('visibilitychange', handleTabVisible);

export const notifyActiveTimerChanged = () => {
  channel.postMessage({ at: Date.now() });
};

export interface ActiveTimerWithFetchedAt {
  timer: ActiveTimerResponse;
  fetchedAt: number;
}

export const useActiveTimer = () => {
  const queryClient = useQueryClient();

  // Keep the singleton reference up to date. The QueryClient instance is stable
  // for the lifetime of the app, so this is effectively a one-time assignment.
  useEffect(() => {
    singletonQueryClient = queryClient;
  }, [queryClient]);

  return useQuery({
    queryKey: ACTIVE_TIMER_QUERY_KEYS.all,
    queryFn: async (): Promise<ActiveTimerWithFetchedAt | null> => {
      const timer = await getActiveTimer();
      if (!timer) return null;
      // Wall-clock stamp so computeElapsedSeconds self-corrects after a long
      // tab suspension instead of freezing on a monotonic delta (OPS-1538/1534).
      return { timer, fetchedAt: Date.now() };
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    // Timer data only changes via mutations or FCM push — no need to treat it
    // as instantly stale. This prevents window-focus from firing a fetch on every
    // tab switch, which compounds badly when many project-cards are mounted.
    staleTime: 30_000,
    // Keep the cache entry while the tab is backgrounded so all three surfaces
    // read the same entry on return (the visibilitychange listener triggers a
    // refetch to refresh it).
    gcTime: 1000 * 60 * 60,
  });
};
